import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Switch,
  Select,
  Card,
  Row,
  Col,
  Button,
  Space
} from "antd";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { DeleteOutlined, HolderOutlined } from "@ant-design/icons";


const { Option } = Select;

const SortableItem = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    marginBottom: 12,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ dragHandleProps: { ...attributes, ...listeners } })}
    </div>
  );
};

const AddEditModal = ({
  isModalVisible,
  handleOk,
  handleCancel,
  loading,
  initialValues = {},
  modelType,
}) => {
  const [form] = Form.useForm();
  const [options, setOptions] = useState([]);

  const sensors = useSensors(useSensor(PointerSensor));

  const mergedInitialValues = {
    ...initialValues,
    is_active:
      modelType === "edit"
        ? Boolean(initialValues?.is_active)
        : true,
    is_required_question: initialValues?.is_required_question ?? false,
    enable_multiple: initialValues?.enable_multiple ?? false
  };

  useEffect(() => {
    form.resetFields();
    if (isModalVisible && modelType === "edit" && initialValues?.question_options) {
      const keys = initialValues.question_options.map((opt, index) => {
        return opt.key || `opt-${index}`;
      });

      setOptions(keys.map((key) => ({ key })));

      const optionByKey = {};
      initialValues.question_options.forEach((opt, index) => {
        const key = keys[index];
        optionByKey[key] = {
          id: opt.id,
          name: opt.name,
          option_order: opt.option_order,
          is_active: opt.is_active,
          unselect_other_option: opt.unselect_other_option,
          is_other: opt.is_other,
          other_input_type: opt.other_input_type,
          other_input_type_max_value: opt.other_input_type_max_value,
        };
      });

      form.setFieldsValue({
        ...mergedInitialValues,
        question_options_by_key: optionByKey,
      });
    } else if (isModalVisible && modelType === "add") {
      console.log("mergedInitialValues==", mergedInitialValues)
      form.setFieldsValue(mergedInitialValues);
      addOption();
    } else if (!isModalVisible) {
      // Cleanup
      setOptions([]);
      // form.setFieldsValue(mergedInitialValues);
    }
  }, [isModalVisible, modelType, initialValues]);



  const addOption = () => {
    const key = Date.now().toString();
    const current = form.getFieldValue("question_options_by_key") || {};

    // New option object with full structure
    const newOption = {
      name: "",
      option_order: Object.keys(current).length + 1,
      is_active: true,
      unselect_other_option: false,
      is_other: false,
      other_input_type: undefined,
      other_input_type_max_value: undefined,
    };

    // Add key to options state first
    setOptions((prev) => [...prev, { key }]);

    // Set full structure immediately (no setTimeout needed)
    const updated = {
      ...current,
      [key]: newOption,
    };

    form.setFieldsValue({ question_options_by_key: updated });
  };



  const removeOption = (key) => {
    const updatedOptions = options.filter((opt) => opt.key !== key);
    setOptions(updatedOptions);

    const current = form.getFieldValue("question_options_by_key") || {};
    delete current[key];
    form.setFieldsValue({ question_options_by_key: { ...current } });
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;

    const oldIndex = options.findIndex((item) => item.key === active.id);
    const newIndex = options.findIndex((item) => item.key === over.id);
    const newOptions = arrayMove(options, oldIndex, newIndex);

    setOptions(newOptions);

    const current = form.getFieldValue("question_options_by_key") || {};
    const updatedOptionsByKey = { ...current };

    newOptions.forEach((item, index) => {
      if (updatedOptionsByKey[item.key]) {
        updatedOptionsByKey[item.key].option_order = index + 1;
      }
    });

    form.setFieldsValue({ question_options_by_key: updatedOptionsByKey });
  };

  const onFinish = (values) => {
    const optionEntries = values.question_options_by_key || {};

    const filteredOptions = options
      .map(({ key }, index) => {
        const opt = optionEntries[key];
        if (!opt || !opt.name?.trim()) return null;
        return {
          ...opt,
          option_order: index + 1, // ✅ Ensure it's explicitly set
        };

      })
      .filter(Boolean);

    if (values.question_type === "option") {
      if (filteredOptions.length === 0){
        Modal.error({
          title: "Validation Error",
          content: "At least one option is required for 'Option' type questions.",
        });
        return;
      }

      // 🔴 Validation: At least one active option
      const hasActiveOption = filteredOptions.some((opt) => opt.is_active);
      if (!hasActiveOption) {
        Modal.error({
          title: "Validation Error",
          content: "At least one option must be active for 'Option' type questions.",
        });
        return;
      }
    }

    const finalPayload = {
      ...values,
      question_options: filteredOptions,
    };

    console.log(finalPayload)
    handleOk(initialValues?.id || null, finalPayload);
  };

  return (
    <Modal
      title={modelType === "add" ? "Add Form Questions" : "Edit Form Question"}
      open={isModalVisible}
      footer={null}
      onCancel={() => {
        handleCancel();
        form.resetFields();
        setOptions([]);
      }}
      maskClosable={false}
      width="70%"
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={mergedInitialValues}
        onFinish={onFinish}
      >
        <Card title="Question Configuration" style={{
          boxShadow: '0px 0px 2px #6e6e6eb8'
        }}
        >

          <Row gutter={16}>
            <Col xs={24} sm={24} md={15} lg={17}>
              <Form.Item
                label="Question"
                name="question"
                rules={[
                  { required: true, message: "Please enter question", },
                  {
                    validator: (_, value) => {
                      if (value === undefined) return Promise.resolve(); // let required rule handle it
                      if (value && value.trim() === "") {
                        return Promise.reject(new Error("Question cannot be empty or whitespace"));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Enter question" maxLength={255} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={9} lg={7}>
              <Form.Item
                label="Question Order"
                name="question_order"
                rules={[
                  { required: true, message: "Enter order" },
                  {
                    validator: (_, value) => {
                      if (value === undefined || value === null || value === "") {
                        return Promise.resolve(); // Let required rule handle it
                      }

                      return value >= 0
                        ? Promise.resolve()
                        : Promise.reject(new Error("Value cannot be negative"));
                    }

                  }
                ]}
              >
                <Input type="number" placeholder="Enter order" min={1} max={1000} />
              </Form.Item>
            </Col>
            {/* <Col span={8}>
                <Form.Item label="Display Text" name="question_display_text">
                  <Input placeholder="Optional display text" />
                </Form.Item>
              </Col> */}
            <Col xs={24} sm={12} md={9} lg={7} xl={7}>
              <Form.Item
                label="Question Type"
                name="question_type"
                rules={[{ required: true, message: "Select type" }]}
              >
                <Select placeholder="Select type">
                  <Option value="input">Input</Option>
                  <Option value="option">Option</Option>
                </Select>
              </Form.Item>
            </Col>
            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.question_type !== curr.question_type}>
              {({ getFieldValue }) =>
                getFieldValue("question_type") === "input" ? (
                  <Col xs={24} sm={24} md={15} lg={10} xl={8}>
                    <Form.Item label="Input Type & Max Length" required>
                      <Space.Compact block>
                        <Form.Item
                          // label="input type"
                          noStyle
                          name="input_type"
                          initialValue="string" 
                          rules={[{ required: true, message: "input type required"}]}
                        >
                          <Select style={{ width: '40%' }}>
                            <Option value="string">String</Option>
                            <Option value="number">Number</Option>
                          </Select>
                        </Form.Item>
                        <Form.Item
                          // label="Max Input Length"
                          noStyle
                          name="max_input_value"
                          rules={[
                            { required: true, message: "Enter max length" },
                            {
                              validator: (_, value) => {
                                if (value === undefined || value === null || value === "") {
                                  return Promise.resolve(); // Let required rule handle it
                                }

                                return value >= 0
                                  ? Promise.resolve()
                                  : Promise.reject(new Error("Value cannot be negative"));
                              }

                            }
                          ]}
                        >
                          <Input type="number" min={1} max={1000} placeholder="Max characters" style={{ width: '60%' }} />
                        </Form.Item>
                      </Space.Compact>
                    </Form.Item>
                  </Col>
                ) : null
              }
            </Form.Item>
            <Col xs={24} sm={12} md={6} lg={6} xl={4} style={{ display: "flex", padding: '0px'}}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, margin:"10px"}}>
                <span>Active</span>
                <Form.Item
                  name="is_active"
                  valuePropName="checked"
                  noStyle
                >
                  <Switch />
                </Form.Item>
              </div>
            </Col>
            <Col xs={24} sm={12} md={6} lg={6} xl={4} style={{ display: "flex", padding: '0px'}}>
              <div style={{ display: "flex", alignItems: "center", gap: 5, margin:"10px"}}>
                <span>Required</span>
                <Form.Item
                  name="is_required_question"
                  noStyle
                  valuePropName="checked"
                >
                  <Switch />
                </Form.Item>
              </div>
            </Col>

            <Form.Item noStyle shouldUpdate={(prev, curr) => prev.question_type !== curr.question_type}>
              {({ getFieldValue }) =>
                getFieldValue("question_type") === "option" ? (
                  <Col xs={24} sm={24} md={24} lg={10} xl={8}  style={{ display: "flex", padding: '0px', marginLeft:'10px'}}>
                    <div style={{ display: "flex", alignItems: "center", gap: 5 , margin:"10px"}}>
                      <span>Multiple Selection </span>
                      <Form.Item
                        noStyle
                        name="enable_multiple"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </div>
                  </Col>
                ) : null
              }
            </Form.Item>
          </Row>

        </Card>

        <Form.Item noStyle shouldUpdate={(prev, curr) => prev.question_type !== curr.question_type}>
          {({ getFieldValue }) =>
            getFieldValue("question_type") === "option" ? (
              <Card
                title="Options"
                style={{ boxShadow: '0px 0px 2px #6e6e6eb8', marginTop: 24 }}
                extra={<Button type="primary" onClick={addOption}>+ Add Option</Button>}
                bodyStyle={{ padding: '12px' }}
              >
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onDragEnd}
                >
                  <SortableContext
                    items={options.map((opt) => opt.key)}
                    strategy={verticalListSortingStrategy}
                  >
                    {options.length === 0 ? (
                      <div style={{ textAlign: "center", color: "#999" }}>
                        No options yet. Click "Add Option".
                      </div>
                    ) : (
                      options.map(({ key }, index) => (
                        <SortableItem key={key} id={key}>
                          {({ dragHandleProps }) => (
                            <div style={{
                              background: "#f8f8f8",
                              borderRadius: "15px"
                            }}>

                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                padding: '8px',
                                paddingBottom: '0px',
                                height: '50px',
                                borderBottom: '1px solid #eee4e4'
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  gap: '12px',
                                  alignItems: 'center',
                                  fontWeight: '600'
                                }}>
                                  <Col span={1}>
                                    <HolderOutlined style={{cursor: 'move',}} {...dragHandleProps} />
                                  </Col>
                                  <p>Option {index + 1}</p>
                                </div>

                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                  gap: '20px',
                                }}>

                                  <Form.Item noStyle shouldUpdate>
                                    {({ getFieldValue }) => {
                                      const value = getFieldValue(["question_options_by_key", key, "is_active"]);

                                      return (
                                        <Form.Item
                                          name={["question_options_by_key", key, "is_active"]}
                                          valuePropName="checked"
                                          style={{ margin: 0 }}
                                        >
                                            <Switch  />
                                        </Form.Item>
                                      );
                                    }}
                                  </Form.Item>



                                  <Button
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() => removeOption(key)}
                                  />
                                </div>

                              </div>

                              <Row
                                gutter={8}
                                align="middle"
                                style={{padding: "8px 15px 0 15px"}}
                              >

                                <Col  xs={24} sm={24} md={15} lg={6}>
                                  <Form.Item
                                    name={["question_options_by_key", key, "name"]}
                                    rules={[
                                      { required: true, message: "Label required" },
                                      {
                                        validator: (_, value) =>
                                          value && value.trim() !== ""
                                            ? Promise.resolve()
                                            : Promise.reject(new Error("Option label cannot be empty or whitespace")),
                                      },
                                    ]}
                                    label={`Option Name`}
                                  >
                                    <Input maxLength={255}  placeholder="Enter option" />

                                  </Form.Item>
                                  <Form.Item
                                    name={["question_options_by_key", key, "id"]}
                                    hidden
                                  >
                                    <Input />
                                  </Form.Item>
                                  <Form.Item
                                    name={["question_options_by_key", key, "option_order"]}
                                    hidden
                                  >
                                    <Input />
                                  </Form.Item>
                                </Col>

                                <Form.Item noStyle shouldUpdate>
                                  {({ getFieldValue }) =>
                                    getFieldValue("enable_multiple") ? (

                                      <Col xs={24} sm={12} md={8} lg={4}>

                                        <Form.Item
                                          name={["question_options_by_key", key, "unselect_other_option"]}
                                          label="Unselect Others"
                                          valuePropName="checked"
                                          initialValue={false}
                                        >
                                          <Switch />
                                        </Form.Item>
                                      </Col>
                                    ) : null}
                                    </Form.Item>

                                <Col  xs={24} sm={12} md={8} lg={4}>
                                  <Form.Item
                                    name={["question_options_by_key", key, "is_other"]}
                                    label="Enable Other"
                                    valuePropName="checked"
                                    initialValue={false}
                                  >
                                    <Switch />
                                  </Form.Item>
                                </Col>

                                <Form.Item noStyle shouldUpdate>
                                  {({ getFieldValue }) =>
                                    getFieldValue(["question_options_by_key", key, "is_other"]) ? (
                                      <>
                                        <Col xs={24} sm={24} md={12} lg={9}>
                                          
                                          <Form.Item label="Other Input Type & Max Length" required>
                                            <Space.Compact block>

                                              <Form.Item
                                                // label="Other Input Type"
                                                noStyle
                                                name={["question_options_by_key", key, "other_input_type"]}
                                                rules={[{ required: true, message: "Select type" }]}
                                                initialValue="string"
                                              >
                                                <Select placeholder="Text or Number" style={{ width: '40%' }}>
                                                  <Option value="string">String</Option>
                                                  <Option value="number">Number</Option>
                                                </Select>
                                              </Form.Item>

                                              <Form.Item
                                                // label="Max Length"
                                                noStyle
                                                name={["question_options_by_key", key, "other_input_type_max_value"]}
                                                rules={[
                                                  { required: true, message: "Max Length" },
                                                  {
                                                    validator: (_, value) => {
                                                      if (value === undefined || value === null || value === "") {
                                                        return Promise.resolve(); // Let required rule handle it
                                                      }

                                                      return value >= 0
                                                        ? Promise.resolve()
                                                        : Promise.reject(new Error("Value cannot be negative"));
                                                    }

                                                  }
                                                ]}
                                              >
                                                <Input type="number" min={1} max={1000} placeholder="e.g. 100" style={{ width: '60%' }} />
                                              </Form.Item>

                                            </Space.Compact>
                                          </Form.Item>

                                        </Col>
                                        {/* <Col span={4}> */}
                                          
                                        {/* </Col> */}
                                      </>
                                    ) : null
                                  }
                                </Form.Item>

                              </Row>
                            </div>
                          )}
                        </SortableItem>
                      ))
                    )}
                  </SortableContext>
                </DndContext>
              </Card>
            ) : null
          }
        </Form.Item>

        <Form.Item style={{ marginTop: 16 }}>
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              {modelType === "add" ? "Add Question" : "Update Question"}
            </Button>
          </div>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddEditModal;
